import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Sparkles, Plus, Edit3, Trash2, Globe, EyeOff, Image as ImageIcon, X } from "lucide-react";
import api from "../../lib/api";
import ImagePicker from "../../components/ImagePicker";
import RichTextEditor from "../../components/RichTextEditor";

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/blog"); setPosts(data); };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blog/generate");
      toast.success(`Draft generated: "${data.title}"`);
      await load();
      setEditing(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); } finally { setBusy(false); }
  };

  const save = async (p) => {
    try {
      if (p.id) await api.patch(`/admin/blog/${p.id}`, p);
      else await api.post("/admin/blog", p);
      toast.success("Saved"); setEditing(null); await load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!confirm("Delete post?")) return; await api.delete(`/admin/blog/${id}`); await load(); };

  return (
    <div data-testid="admin-blog-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">automated weekly posts</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Blog</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={generate} disabled={busy} className="sac-btn-primary" data-testid="blog-generate-btn"><Sparkles size={16} /> {busy ? "Drafting…" : "Generate weekly post"}</button>
          <button onClick={() => setEditing({ title: "", excerpt: "", body_html: "", tags: [], status: "draft" })} className="sac-btn-secondary" data-testid="blog-add-btn"><Plus size={16} /> New post</button>
        </div>
      </div>

      <div className="admin-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-left text-[#5D2C8F]">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">AI</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-[#E5E0D8] hover:bg-[#FAF6EE]/60 cursor-pointer transition-colors" onClick={() => setEditing(p)} data-testid={`blog-row-${p.id}`}>
                <td className="px-4 py-3 text-[#5D2C8F]">
                  <div className="font-medium hover:underline">{p.title}</div>
                  <div className="text-xs text-[#6E6074] line-clamp-1 max-w-lg">{p.excerpt}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${p.status === 'published' ? 'bg-[#8EC9C7]/20 text-[#5D2C8F]' : 'bg-[#E5E0D8] text-[#6E6074]'}`}>
                    {p.status === 'published' ? <Globe size={11} /> : <EyeOff size={11} />} {p.status}
                  </span>
                </td>
                <td className="px-4 py-3">{p.ai_generated && <span className="text-[#A685BD] text-xs">✨ AI</span>}</td>
                <td className="px-4 py-3 text-[#6E6074]">{new Date(p.created_at).toLocaleDateString('en-GB')}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setEditing(p)} className="p-1.5" data-testid={`blog-edit-${p.id}`} title="Edit post"><Edit3 size={14} /></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-[#A685BD]" data-testid={`blog-delete-${p.id}`} title="Delete post"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6E6074]">No posts yet. Click <em>Generate weekly post</em> for your first AI-drafted article.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <PostEditor post={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function PostEditor({ post, onClose, onSave }) {
  const [p, setP] = useState({ ...post, tags: post.tags || [] });
  const [showPicker, setShowPicker] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="admin-surface w-full max-w-4xl p-6 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="blog-editor-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-4">{p.id ? 'Edit post' : 'New post'}</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-sm text-[#5D2C8F]">Title</label>
            <input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 font-display text-lg" data-testid="blog-editor-title" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-[#5D2C8F]">Excerpt</label>
            <input value={p.excerpt} onChange={(e) => setP({ ...p, excerpt: e.target.value })} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="blog-editor-excerpt" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-[#5D2C8F]">Cover image</label>
            <div className="flex items-stretch gap-2 mt-1">
              <input value={p.cover_image || ""} onChange={(e) => setP({ ...p, cover_image: e.target.value })} placeholder="Paste a URL, or pick a class photo →"
                className="flex-1 bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="blog-editor-cover" />
              <button onClick={() => setShowPicker(true)} className="sac-btn-secondary !py-2 !px-4 text-sm whitespace-nowrap" data-testid="blog-editor-cover-pick">
                <ImageIcon size={14} /> Pick from library
              </button>
            </div>
            {p.cover_image && (
              <div className="mt-2 relative inline-block">
                <img src={p.cover_image} alt="Cover preview" className="h-32 rounded-lg border border-[#E5E0D8] object-cover" />
                <button onClick={() => setP({ ...p, cover_image: "" })} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-[#A685BD]" aria-label="Clear cover image" data-testid="blog-editor-cover-clear"><X size={12} /></button>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-[#5D2C8F]">Tags (comma separated)</label>
            <input value={(p.tags || []).join(", ")} onChange={(e) => setP({ ...p, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="blog-editor-tags" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-[#5D2C8F]">Body</label>
            <p className="text-xs text-[#6E6074] mt-0.5 mb-1">Use the toolbar for headings, formatting, links and inline images (pulls from your Site Images / Class Photos library).</p>
            <RichTextEditor
              value={p.body_html || ""}
              onChange={(html) => setP({ ...p, body_html: html })}
              placeholder="Write the post…"
              testid="blog-editor-body"
              minHeight={400}
            />
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">Status</label>
            <select value={p.status} onChange={(e) => setP({ ...p, status: e.target.value })} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="blog-editor-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]">Cancel</button>
          <button onClick={() => onSave(p)} className="sac-btn-primary" data-testid="blog-editor-save-btn">Save</button>
        </div>
        {showPicker && (
          <ImagePicker
            open={showPicker}
            onClose={() => setShowPicker(false)}
            onPick={(val) => { setP({ ...p, cover_image: val.src }); setShowPicker(false); }}
            title="Pick a cover image"
          />
        )}
      </div>
    </div>
  );
}
